import { useState } from "react"
import { useTranslation } from "react-i18next"
import InputField from "../../components/InputField/InputField"
import ModalPortal from "../../components/Modal/ModalPortal"
import type { IPublicMenuTheme } from "../Helpers/PublicMenuTheme"
import { PopupHelpButton } from "../../components/Help/PopupHelpButton"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"

interface Props {
  isLoading: boolean
  theme: IPublicMenuTheme
  onClose: () => void
  onJoin: (code: string) => Promise<boolean>
}

export const RodizioJoinPopup: React.FC<Props> = ({ isLoading, theme, onClose, onJoin }) => {
  const { t } = useTranslation("rodizio")

  const [code, setCode] = useState("")

  const normalizedCode = code.replace(/\D/g, "").slice(0, 6)

  return (
    <ModalPortal>
      <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
        <div className="fixed inset-0 z-[200] flex items-end justify-center bg-black/60 p-2 sm:items-center">
          <form
            className="w-full max-w-md rounded-2xl border-2 p-5 shadow-2xl"
            style={{ backgroundColor: theme.surfaceColor, borderColor: theme.primaryColor, color: theme.textColor }}
            onSubmit={async (event) => {
              event.preventDefault()
              if (await onJoin(normalizedCode)) onClose()
            }}
          >
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-xl font-black">{t("public.join_title")}</h2>

              <PopupHelpButton
                title={t("helpPopup:global.rodizioCustomer.title")}
                description={t("helpPopup:global.rodizioCustomer.description")}
                steps={t("helpPopup:global.rodizioCustomer.steps", { returnObjects: true }) as string[]}
                bullets={t("helpPopup:global.rodizioCustomer.bullets", { returnObjects: true }) as string[]}
                buttonBackgroundColor={theme.buttonColor}
                buttonIconColor={theme.buttonTextColor}
              />
            </div>

            <p className="mb-4 mt-1 text-sm font-semibold opacity-75">{t("public.join_description")}</p>

            <InputField
              type="text"
              value={normalizedCode}
              required
              minLength={6}
              maxLength={6}
              inputMode="numeric"
              pattern="[0-9]{6}"
              placeholder={t("public.access_code")}
              onChange={(event) => setCode(event.target.value)}
              descriptionKey="interactionDescriptions:rodizio.public.accessCode"
            />

            <div className="mt-5 grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={onClose}
                className="min-h-11 rounded-xl border-2 px-4 font-black"
                style={{ borderColor: theme.borderColor }}
              >
                {t("public.cancel")}
              </button>

              <button
                type="submit"
                disabled={isLoading || normalizedCode.length !== 6}
                className="min-h-11 rounded-xl px-4 font-black disabled:opacity-50"
                style={{ backgroundColor: theme.buttonColor, color: theme.buttonTextColor }}
              >
                {isLoading ? t("public.joining") : t("public.join")}
              </button>
            </div>
          </form>
        </div>
      </Animate>
    </ModalPortal>
  )
}
